// Copyright (C) 2021-Present CITEC Inc. <https://citecsolutions.com/>
// All rights reserved
//
// This file is part of CITEC Inc. source code.
// This software framework contains the confidential and proprietary information
// of CITEC Inc., its affiliates, and its licensors. Your use of these
// materials is governed by the terms of the Agreement between your organization
// and CITEC Inc., and any unauthorized use is forbidden. Except as otherwise
// stated in the Agreement, this software framework is for your internal use
// only and may only be shared outside your organization with the prior written
// permission of CITEC Inc.
// CITEC Inc. source code can not be copied and/or distributed without the express
// permission of CITEC Inc.

import { groupInsights } from './group-insights';
import {
  type InsightPayload,
  type Intent,
} from '@shared/data-access/interfaces/api/copilot/chat';
import { montecarloApi } from '@advisor-app/store/api/ai/montecarlo-api';
import { reportingApi } from '@shared/data-access/api/ai/reporting-api';
import { portfolioApi } from '@shared/data-access/api/copilot/portfolio-api';
import { type WorkflowAsset } from '@shared/data-access/interfaces/redux/workflow-state';
import { type EnhancedPortfolioResponse } from '@shared/data-access/interfaces/api/copilot/portfolio';
import { type ThunkDispatch } from '@reduxjs/toolkit';

export const getCopilotContext = async (
  intent: Intent,
  payload: InsightPayload,
  assetType: WorkflowAsset,
  dispatch: ThunkDispatch<any, any, any>,
  onEnhancement?: (enhancement: EnhancedPortfolioResponse) => void,
) => {
  const validContextIntents = ['asset_comparison', 'portfolio', 'montecarlo'];

  if (!validContextIntents.includes(intent)) {
    return '';
  }

  let context = '';

  try {
    if (intent === 'portfolio' && payload.portfolio) {
      const insightsApiResp = await dispatch(
        reportingApi.endpoints.portfolioInsights.initiate({
          payload: payload.portfolio,
          assetType,
        }),
      ).unwrap();

      const groupedInsights = groupInsights(insightsApiResp);

      context = groupedInsights;

      dispatch(
        portfolioApi.endpoints.portfolioEnhancement.initiate({
          payload: insightsApiResp,
          html_response: false,
        }),
      )
        .unwrap()
        .then(
          onEnhancement,
          /* istanbul ignore next */ error =>
            console.error('[getCopilotContext]', error),
        );
    } else if (intent === 'montecarlo' && payload.simulation) {
      const action = montecarloApi.endpoints.insights.initiate({
        payload: payload.simulation,
      });
      const insightsApiResp = await dispatch(action).unwrap();

      const groupedInsights = groupInsights(insightsApiResp);

      context = groupedInsights;
    } else if (intent === 'asset_comparison' && payload.comparison) {
      const action = reportingApi.endpoints.comparisonInsights.initiate(
        payload.comparison,
      );
      const insightsApiResp = await dispatch(action).unwrap();

      const groupedInsights = groupInsights(insightsApiResp);

      context = groupedInsights;
    }
  } catch (error) {
    console.error('[getCopilotContext]', error);
  }

  return context;
};
